import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState, type FormEvent } from 'react'
import { ApiError } from '../api/client'
import { createExternalProvider, externalProviderQuery, saveExternalProvider } from '../api/easeedesk'
import { Modal } from '../ui/Modal'
import { Spinner } from '../ui/Spinner'
import { toast } from '../ui/notify'

/**
 * Furnizorul extern din fereastra de escaladare - `tickets/details/modals.php`:
 * `#createExternalProviderModal` ("Create external service provider": nume,
 * email, telefon, `easeedesk/createExternalProvider`) si
 * `#editExternalProviderModal` ("Edit external service provider", cu
 * campurile umplute din `getExternalProviderSettings`,
 * `easeedesk/saveExternalProviderSettings`).
 *
 * Dupa salvare, lista de furnizori din formular se cere din nou
 * (`refreshExternalProvider('list')`): formularul de escaladare se reciteste.
 */
export function ExternalProviderModal({ ticketId, providerId, onClose, onSaved }: {
  ticketId: number
  /** 0 = furnizor nou. */
  providerId: number
  onClose(): void
  onSaved(id: number): void
}) {
  const editing = providerId > 0
  const current = useQuery({ ...externalProviderQuery(providerId), enabled: editing })
  const client = useQueryClient()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (current.data) {
      setName(current.data.name)
      setEmail(current.data.email)
      setPhone(current.data.phone)
    }
  }, [current.data])

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    const fields = { provider_name: name, provider_email: email, provider_phone: phone }
    const call = editing ? saveExternalProvider(providerId, fields) : createExternalProvider(fields)
    call
      .then((out) => {
        toast(editing ? 'External provider settings saved.' : 'External provider created.', 'Success')
        void client.invalidateQueries({ queryKey: ['easeedesk', 'ticket', ticketId, 'action-form'] })
        void client.invalidateQueries({ queryKey: ['easeedesk', 'external-provider', providerId] })
        onSaved(out.id)
      })
      .catch((err: unknown) => toast(err instanceof ApiError && err.message ? err.message : 'Something went wrong.', 'Error', 'error'))
      .finally(() => setBusy(false))
  }

  const id = editing ? 'editExternalProviderModal' : 'createExternalProviderModal'
  const formId = editing ? 'editExternalProviderForm' : 'createExternalProviderForm'

  return (
    <Modal
      id={id}
      open
      onClose={onClose}
      title={editing ? 'Edit external service provider' : 'Create external service provider'}
      labelledBy={`${id}Label`}
      footer={
        <>
          <button type="button" className="btn btn-default" onClick={onClose}>
            Close
          </button>
          <button type="submit" form={formId} className="btn btn-primary" id={editing ? 'saveExternalProvider' : 'createExternalProvider'} disabled={busy}>
            {editing ? 'Save' : 'Create'}
          </button>
        </>
      }
    >
      {editing && !current.data ? (
        current.error ? (
          <div className="alert alert-danger">
            <i className="fas fa-exclamation-circle" aria-hidden="true"></i> {current.error.message || 'Not found.'}
          </div>
        ) : (
          <Spinner />
        )
      ) : (
        <form id={formId} className="form-horizontal" onSubmit={submit}>
          <div className="form-group">
            <label className="col-sm-3 control-label" htmlFor={`${formId}-name`}>
              Name <span className="required">*</span>
            </label>
            <div className="col-sm-9">
              <input id={`${formId}-name`} name="provider_name" className="form-control" required value={name} onChange={(e) => setName(e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" htmlFor={`${formId}-email`}>
              Email
            </label>
            <div className="col-sm-9">
              <input id={`${formId}-email`} name="provider_email" type="email" className="form-control" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" htmlFor={`${formId}-phone`}>
              Phone
            </label>
            <div className="col-sm-9">
              <input id={`${formId}-phone`} name="provider_phone" className="form-control" value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>
          </div>
        </form>
      )}
    </Modal>
  )
}

/**
 * Sub casuta "External Provider" din escaladare (`#external_provider_details`,
 * `refreshExternalProvider('settings')`): detaliile furnizorului ales si
 * butonul Edit; langa ea, "Add" pentru un furnizor nou (`addExternalProvider`).
 */
export function ExternalProviderDetails({ ticketId, providerId, onPick }: { ticketId: number; providerId: number; onPick(id: number): void }) {
  const [editing, setEditing] = useState<number | null>(null)
  const details = useQuery({ ...externalProviderQuery(providerId), enabled: providerId > 0 })

  return (
    <div className="form-group">
      <span></span>
      <div id="external_provider_details" style={{ position: 'relative' }}>
        <button type="button" className="btn btn-xs btn-default" id="addExternalProvider" onClick={() => setEditing(0)}>
          <i className="fas fa-plus" aria-hidden="true"></i> Add
        </button>{' '}
        {providerId > 0 ? (
          <button type="button" className="btn btn-xs btn-primary" data-external-provider={providerId} onClick={() => setEditing(providerId)}>
            Edit
          </button>
        ) : null}
        {providerId > 0 && details.data ? (
          <>
            <p>Name: {details.data.name || 'n/a'}</p>
            <p>Email: {details.data.email || 'n/a'}</p>
            <p>Phone: {details.data.phone || 'n/a'}</p>
          </>
        ) : null}
      </div>
      {editing !== null ? (
        <ExternalProviderModal
          ticketId={ticketId}
          providerId={editing}
          onClose={() => setEditing(null)}
          onSaved={(id) => {
            setEditing(null)
            onPick(id)
          }}
        />
      ) : null}
    </div>
  )
}
